import { callUnaryMethod } from '@connectrpc/connect-query'

import type { ClientEvent } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/event/v1/event.v1_pb'
import { publish } from '@jotta/grpc-connect-openapi/eventQuery'

import { authTransport } from './transport'

export type ClientEventProperties = Record<string, string | number | boolean>

let enableClientEvents = false
let visitId: string | undefined

export function setEnableClientEvents(enabled: boolean): void {
  enableClientEvents = enabled
}

/**
 * Generate a unique ID that is persisted with module scope
 * This avoids complexity with persistence and invalidation
 * However it will not persist across multiple tabs and browser refresh
 */
function getVisitId() {
  visitId ??= crypto.randomUUID()
  return visitId
}

/**
 * Prepares a record to be sent as a tags value
 */
function recordToTag(
  properties: ClientEventProperties | undefined,
): ClientEvent['tags'] {
  if (!properties) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, String(value)]),
  )
}

/**
 * Publishes a client analytics event through the event service.
 */
async function publishAnalyticsEvent(
  eventName: string,
  properties?: ClientEventProperties,
): Promise<void> {
  await callUnaryMethod(authTransport, publish, {
    type: eventName,
    tags: recordToTag(properties),
    visitId: getVisitId(),
  })
}

/**
 * Send a client analytics event.
 *
 * If client event publishing has not been enabled, the call is dropped.
 */
export function trackEvent(
  eventName: string,
  properties?: ClientEventProperties,
): void {
  if (!enableClientEvents) {
    return
  }

  publishAnalyticsEvent(eventName, properties).catch(error => {
    reportError(error)
    console.error('Error publishing analytics event:', error)
  })
}
